import { useEffect, useRef } from "react";
import type { StoreApi } from "zustand/vanilla";

/** Shape of a store created with `subscribeWithSelector`. */
type SelectorStore<S> = StoreApi<S> & {
  subscribe: {
    <U>(
      selector: (state: S) => U,
      listener: (selected: U, previous: U) => void,
      options?: { equalityFn?: (a: U, b: U) => boolean; fireImmediately?: boolean },
    ): () => void;
  };
};

/**
 * Run `effect` whenever `selector(state)` changes, WITHOUT re-rendering the component.
 * Use it for anything driven by high-frequency live data (playhead, transcript highlight,
 * live numbers): the effect writes to DOM refs / chart instances directly.
 *
 * `effect` always sees the latest closure (kept in a ref), so it can be an inline function.
 * Pass `equalityFn` when the selector returns a new object/array (e.g. a tuple of two fields).
 */
export function useStoreEffect<S, U>(
  store: SelectorStore<S>,
  selector: (state: S) => U,
  effect: (value: U, previous: U) => void,
  { fireImmediately = true, equalityFn }: { fireImmediately?: boolean; equalityFn?: (a: U, b: U) => boolean } = {},
) {
  const effectRef = useRef(effect);
  effectRef.current = effect;
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const equalRef = useRef(equalityFn);
  equalRef.current = equalityFn;

  useEffect(
    () =>
      store.subscribe(
        (s) => selectorRef.current(s),
        (v, prev) => effectRef.current(v, prev),
        { fireImmediately, equalityFn: (a, b) => (equalRef.current ? equalRef.current(a, b) : Object.is(a, b)) },
      ),
    [store, fireImmediately],
  );
}
